/**
 * The health guard pauses automatic retuning across the fleet when SoundOps
 * itself is overloaded or its health reports are missing
 * (`www/api/services/surveillance_health_guard.py`).
 */
import {
  defineVocabulary,
  lowerCaseCode,
  UNRECOGNISED_REASON_TEXT,
  UNRECOGNISED_STATUS_TEXT,
} from './core';

export type HealthGuardStateCode = 'healthy' | 'degraded' | 'unknown';

export const healthGuardStateVocabulary = defineVocabulary<HealthGuardStateCode>({
  domain: 'System checks',
  unrecognisedText: UNRECOGNISED_STATUS_TEXT,
  normalize: lowerCaseCode,
  labels: {
    healthy: 'System checks passing',
    degraded: 'System checks need attention',
    unknown: 'System checks not reported',
  },
});

export type HealthGuardReasonCode =
  | 'backlog'
  | 'surveillance_backlog'
  | 'cpu'
  | 'cpu_pressure'
  | 'connection_registry_unavailable'
  | 'connection_registry_invalid'
  | 'system_health_unavailable'
  | 'system_health_timestamp_invalid'
  | 'system_health_timestamp_in_future'
  | 'system_health_snapshot_stale'
  | 'system_health_status_invalid'
  | 'system_health_unknown'
  | 'system_health_critical'
  | 'system_health_warning'
  | 'drift_alerts'
  | 'drift_alerts_warning'
  | 'calibration_stale'
  | 'calibration_warning'
  | 'calibration_low_samples'
  | 'calibration_low_samples_warning'
  | 'pipeline_metadata_missing'
  | 'calibration_missing';

const BACKLOG = 'Too much audio is waiting to be processed';
const BUSY = 'SoundOps servers are too busy';
const HEALTH_REPORT_UNREADABLE = 'The system health report could not be read';

export const healthGuardReasonVocabulary = defineVocabulary<HealthGuardReasonCode>({
  domain: 'System check reason',
  unrecognisedText: UNRECOGNISED_REASON_TEXT,
  normalize: lowerCaseCode,
  labels: {
    backlog: BACKLOG,
    surveillance_backlog: BACKLOG,
    cpu: BUSY,
    cpu_pressure: BUSY,
    connection_registry_unavailable: 'The live audio service is not reporting',
    connection_registry_invalid: 'The live audio service sent a report SoundOps could not read',
    system_health_unavailable: 'The system health report is missing',
    system_health_timestamp_invalid: HEALTH_REPORT_UNREADABLE,
    system_health_timestamp_in_future: 'The system health report has a time in the future',
    system_health_snapshot_stale: 'The system health report is out of date',
    system_health_status_invalid: HEALTH_REPORT_UNREADABLE,
    system_health_unknown: 'System health is unknown',
    system_health_critical: 'System health is critical',
    system_health_warning: 'System health has a warning',
    drift_alerts: 'Too many devices report a serious change in sound',
    drift_alerts_warning: 'Several devices report a change in sound',
    calibration_stale: 'Many models have out-of-date alert levels',
    calibration_warning: 'Some models have out-of-date alert levels',
    calibration_low_samples: 'Many models set their alert level from too little audio',
    calibration_low_samples_warning: 'Some models set their alert level from too little audio',
    pipeline_metadata_missing: 'Some models are missing their audio settings',
    calibration_missing: 'Some models have no alert level set',
  },
});
